import { Fragment, useEffect, useState, useCallback } from "react";
import api, { describeError } from "@/lib/api";
import { fmtEuro, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { SlidersHorizontal, RotateCcw, Loader2, Search, Pencil, Trash2, ChevronDown, ChevronRight, Lock, CalendarClock } from "lucide-react";
import { MemberChips } from "@/components/MemberPicker";
import { toast } from "sonner";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Accepte la virgule décimale (FR/BE) et les espaces de milliers.
const parseNum = (v) => {
  if (v === undefined || v === null || v === "") return NaN;
  const s = String(v).replace(/\s/g, "").replace(",", ".");
  const n = parseFloat(s);
  return isFinite(n) ? n : NaN;
};

export function MonthlyBudgetTable({ year, month, categories, reloadKey, txs = [], members = [], planned = [], onEdit, onDelete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [openIds, setOpenIds] = useState([]);
  const [search, setSearch] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState({});
  const [savingTpl, setSavingTpl] = useState(false);
  const [creditMap, setCreditMap] = useState({});
  const [editPast, setEditPast] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);

  const nowD = new Date();
  const isPast = year < nowD.getFullYear() || (year === nowD.getFullYear() && month < nowD.getMonth() + 1);
  const locked = isPast && !editPast;
  useEffect(() => { setEditPast(false); }, [year, month]);

  useEffect(() => {
    api.get(`/properties/credit-breakdown?year=${year}&month=${month}`)
      .then((r) => setCreditMap(r.data)).catch(() => setCreditMap({}));
  }, [year, month]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/monthly-budget?year=${year}&month=${month}`)
      .then((r) => { setData(r.data); setDrafts({}); setEditingBudget(null); })
      .finally(() => setLoading(false));
  }, [year, month, reloadKey]);
  useEffect(load, [load]);

  const savePlanned = async (row, val) => {
    const amount = parseNum(val);
    if (isNaN(amount)) return;
    try {
      await api.put("/monthly-budget", { year, month, category_id: row.category_id, amount });
      toast.success(`Budget « ${row.category_name} » mis à jour pour ce mois`);
      load();
    } catch (e) {
      console.error("monthly-budget save failed", e?.response?.status, e?.response?.data);
      toast.error(`Enregistrement échoué — ${describeError(e)}`);
      load();
    }
  };

  const reset = async (row) => {
    await api.delete(`/monthly-budget?year=${year}&month=${month}&category_id=${row.category_id}`);
    toast.success("Remis au montant du mois type");
    load();
  };

  // Cellule « Prévu » : lecture seule + crayon, éditable au clic (schéma commun à l'app).
  const renderBudgetEdit = (r, key, testidPrefix) => {
    const val = drafts[key] !== undefined ? drafts[key] : String(r.planned ?? "");
    const editing = editingBudget === key;
    const commit = (v) => { const n = parseNum(v); if (!isNaN(n) && n !== r.planned) savePlanned(r, v); setEditingBudget(null); };
    return (
      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {editing ? (
          <div className="relative w-28">
            <Input autoFocus type="text" inputMode="decimal" value={val}
              onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
              onBlur={(e) => commit(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              className="h-8 text-right pr-5 font-num" data-testid={`${testidPrefix}-input-${key}`} />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
          </div>
        ) : (
          <button type="button" className="group inline-flex items-center gap-1.5 rounded-md px-2 h-8 hover:bg-secondary/60 transition-colors"
            onClick={() => setEditingBudget(key)} data-testid={`${testidPrefix}-value-${key}`}>
            <span className="font-num">{fmtEuro(r.planned)}</span>
            <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
          </button>
        )}
        {r.is_override && !isPast && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => reset(r)} title="Revenir au mois type" data-testid={`${testidPrefix}-reset-${key}`}>
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    );
  };

  const expenseCats = categories.filter((c) => c.type !== "income" && c.scope === "monthly");
  const incomeCats = categories.filter((c) => c.type === "income" && c.scope === "monthly");

  const openTpl = async () => {
    const r = await api.get("/budget-template");
    const map = {};
    r.data.forEach((t) => { map[t.category_id] = String(t.amount); });
    setTpl(map);
    setTplOpen(true);
  };

  const saveTpl = async () => {
    setSavingTpl(true);
    try {
      const items = [];
      for (const c of [...expenseCats, ...incomeCats]) {
        const v = tpl[c.id];
        if (v === undefined || v === "") continue;
        const amount = parseNum(v);
        items.push({ category_id: c.id, amount: isNaN(amount) ? 0 : amount });
      }
      await api.put("/budget-template/bulk", { items });
      toast.success("Mois type enregistré");
      setTplOpen(false);
      load();
    } catch (e) {
      console.error("budget-template save failed", e?.response?.status, e?.response?.data);
      toast.error(`Mois type non enregistré — ${describeError(e)}`);
    } finally { setSavingTpl(false); }
  };

  const q = search.trim().toLowerCase();
  const matchTx = (t) => !q || [t.vendor, t.description, t.region, String(t.amount), t.category_name]
    .some((v) => (v || "").toString().toLowerCase().includes(q));
  const plannedExp = (planned || []).filter((p) => p.type === "expense");
  const plannedIds = new Set(plannedExp.map((p) => p.id));
  const isLinked = (t) => t.planned_id && plannedIds.has(t.planned_id);
  const expByCat = {};
  const incByCat = {};
  txs.forEach((t) => { if (isLinked(t)) return; const k = t.category_id || "__none__"; const m = t.type === "income" ? incByCat : expByCat; (m[k] = m[k] || []).push(t); });
  const txByCat = expByCat;

  const budgetRows = data ? data.rows : [];
  const budgetIds = new Set(budgetRows.map((r) => r.category_id));
  const extraRows = [];
  Object.keys(txByCat).forEach((cid) => {
    if (budgetIds.has(cid)) return;
    const items = txByCat[cid];
    const name = cid === "__none__" ? "Non catégorisé" : (items[0].category_name || (categories.find((c) => c.id === cid) || {}).name || "Autre");
    const spent = round2(items.filter((t) => t.type !== "income").reduce((s, t) => s + t.amount, 0));
    const received = round2(items.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0));
    extraRows.push({ category_id: cid, category_name: name, planned: null, spent, received, is_override: false, extra: true });
  });
  const allRows = [...budgetRows.map((r) => ({ ...r })), ...extraRows];

  const rowTxs = (cid) => (txByCat[cid] || []).filter(matchTx);
  const isVisible = (row) => !q || row.category_name.toLowerCase().includes(q) || rowTxs(row.category_id).length > 0;
  const isOpen = (cid) => (q ? rowTxs(cid).length > 0 : openIds.includes(cid));
  const toggle = (cid) => setOpenIds((o) => (o.includes(cid) ? o.filter((x) => x !== cid) : [...o, cid]));
  const visibleRows = allRows.filter(isVisible);

  const incomeBackendRows = data ? (data.income_rows || []) : [];
  const incomeIds = new Set(incomeBackendRows.map((r) => r.category_id));
  const incomeExtra = [];
  Object.keys(incByCat).forEach((cid) => {
    if (incomeIds.has(cid)) return;
    const items = incByCat[cid];
    const name = cid === "__none__" ? "Rentrée non catégorisée" : (items[0].category_name || (categories.find((c) => c.id === cid) || {}).name || "Autre");
    const received = round2(items.reduce((s, t) => s + t.amount, 0));
    incomeExtra.push({ category_id: cid, category_name: name, planned: null, received, is_override: false, extra: true });
  });
  const incomeAll = [...incomeBackendRows.map((r) => ({ ...r })), ...incomeExtra];
  const incRowTxs = (cid) => (incByCat[cid] || []).filter(matchTx);
  const incVisible = incomeAll.filter((row) => !q || row.category_name.toLowerCase().includes(q) || incRowTxs(row.category_id).length > 0);

  // Cadre "Dépenses exceptionnelles" : uniquement les provisions de la Vue Annuelle & Prévisions + mouvements liés (planned_id).
  const monthlyVisible = visibleRows;
  const txByPlanned = {};
  txs.forEach((t) => { if (isLinked(t)) (txByPlanned[t.planned_id] = txByPlanned[t.planned_id] || []).push(t); });
  const plannedSpentOf = (pid) => round2((txByPlanned[pid] || []).reduce((s, t) => s + t.amount, 0));
  const planAmt = (p) => (p.month_amount != null ? p.month_amount : (p.amount || 0));
  const isDated = (p) => (p.months || []).some((m) => m === 0 || (m >= 1 && m <= 12));
  // Provision non datée (« Non précisé ») : le prévu s'aligne sur le dépensé (écart 0).
  const framePlanned = (p) => (isDated(p) ? planAmt(p) : plannedSpentOf(p.id));
  // Cadre jaune : provisions datées (mois précis / chaque mois) + provisions « Non précisé » ayant un mouvement lié ce mois-ci.
  const frameExp = plannedExp.filter((p) => isDated(p) || plannedSpentOf(p.id) > 0);
  const excPlannedTotal = round2(frameExp.reduce((s, p) => s + framePlanned(p), 0));
  const excSpent = round2(frameExp.reduce((s, p) => s + plannedSpentOf(p.id), 0));
  const hasExceptional = frameExp.length > 0;

  const renderTxLine = (t) => (
    <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0" data-testid={`transaction-row-${t.id}`}>
      <div className="text-xs text-muted-foreground w-20 shrink-0">{fmtDate(t.date)}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate flex items-center gap-2">{t.vendor || t.description || "—"} <MemberChips members={members} ids={t.member_ids} /></div>
        <div className="text-xs text-muted-foreground truncate">{[t.description && t.description !== t.vendor ? t.description : null, t.region].filter(Boolean).join(" · ")}</div>
      </div>
      <div className={`font-num text-sm font-medium whitespace-nowrap ${t.type === "income" ? "text-emerald-600" : ""}`}>{t.type === "income" ? "+" : "-"}{fmtEuro(t.amount)}</div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit && onEdit(t)} data-testid={`edit-tx-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete && onDelete(t.id)} data-testid={`delete-tx-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );

  const renderExpenseRow = (r) => {
    const open = isOpen(r.category_id);
    const items = rowTxs(r.category_id);
    const hasPlan = r.planned !== null && r.planned !== undefined;
    const ecart = hasPlan ? r.planned - r.spent : null;
    const key = r.category_id;
    return (
      <Fragment key={key}>
        <TableRow data-testid={`budget-row-${key}`} className="cursor-pointer hover:bg-secondary/40">
          <TableCell onClick={() => toggle(key)}>
            <div className="flex items-center gap-1.5 font-medium">
              {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              {r.category_name}
              {r.is_override && <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 text-amber-600">modifié</Badge>}
              {items.length > 0 && <span className="text-xs text-muted-foreground font-normal">· {items.length} mvt</span>}
            </div>
          </TableCell>
          <TableCell className="text-right">
            {hasPlan ? (
              locked ? (
                <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()} data-testid={`budget-locked-${key}`}>
                  <Lock className="w-3 h-3 text-muted-foreground" />
                  <span className="font-num">{fmtEuro(r.planned)}</span>
                </div>
              ) : renderBudgetEdit(r, key, "budget")
            ) : <span className="text-muted-foreground">—</span>}
          </TableCell>
          <TableCell className={`text-right font-num ${r.extra && r.received > 0 ? "text-emerald-600" : ""}`} onClick={() => toggle(key)}>
            {r.extra && r.received > 0 ? `+${fmtEuro(r.received)}` : fmtEuro(r.spent)}
          </TableCell>
          <TableCell className={`text-right font-num ${ecart !== null && ecart < 0 ? "text-destructive" : ecart !== null ? "text-emerald-600" : "text-muted-foreground"}`} onClick={() => toggle(key)}>
            {ecart !== null ? fmtEuro(ecart) : "—"}
          </TableCell>
        </TableRow>
        {open && (
          <TableRow className="bg-secondary/20 hover:bg-secondary/20">
            <TableCell colSpan={4} className="p-0">
              <div className="px-4 py-1">
                {items.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-3">Aucun mouvement dans cette catégorie ce mois-ci.</p>
                ) : items.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0" data-testid={`transaction-row-${t.id}`}>
                    <div className="text-xs text-muted-foreground w-20 shrink-0">{fmtDate(t.date)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate flex items-center gap-2">{t.vendor || t.description || "—"} <MemberChips members={members} ids={t.member_ids} /></div>
                      <div className="text-xs text-muted-foreground truncate">{[t.description && t.description !== t.vendor ? t.description : null, t.region].filter(Boolean).join(" · ")}</div>
                      {(creditMap[t.loan_id] || creditMap[t.property_id]) && (() => { const cb = creditMap[t.loan_id] || creditMap[t.property_id]; return (
                        <div className="text-[11px] text-amber-600 mt-0.5 font-num" data-testid={`credit-breakdown-${t.id}`}>
                          {cb.property_name}{cb.loan_name ? ` · ${cb.loan_name}` : ""} — dont capital {fmtEuro(cb.principal)} · intérêts {fmtEuro(cb.interest)} · reste dû {fmtEuro(cb.remaining_balance)}
                        </div>
                      ); })()}
                    </div>
                    <div className={`font-num text-sm font-medium whitespace-nowrap ${t.type === "income" ? "text-emerald-600" : ""}`}>{t.type === "income" ? "+" : "-"}{fmtEuro(t.amount)}</div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit && onEdit(t)} data-testid={`edit-tx-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete && onDelete(t.id)} data-testid={`delete-tx-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };

  return (
    <Card className="p-5 border-border" data-testid="monthly-budget-table">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="font-display font-semibold text-base">Budget par catégorie</h3>
          <p className="text-xs text-muted-foreground">Cliquez une catégorie pour voir ses mouvements. Ajustez le montant prévu si besoin.</p>
        </div>
        <Button variant="outline" size="sm" onClick={openTpl} data-testid="open-template-button">
          <SlidersHorizontal className="w-4 h-4 mr-1.5" />Mois type
        </Button>
      </div>

      {isPast && (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 mb-3 text-sm" data-testid="past-month-banner">
          <span className="flex items-center gap-1.5 text-amber-700"><Lock className="w-3.5 h-3.5 shrink-0" />Mois clôturé — les montants prévus sont figés. Modifier le mois type n'affectera pas ce mois.</span>
          <Button variant="outline" size="sm" onClick={() => setEditPast((v) => !v)} data-testid="toggle-edit-past">{editPast ? "Terminer" : "Modifier"}</Button>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2 mb-3 text-sm" data-testid="monthly-remaining">
          <span className="font-medium">Reste à dépenser ce mois-ci (selon le prévu)</span>
          <span className={`font-num font-semibold ${data.total_planned - data.total_spent < 0 ? "text-destructive" : "text-emerald-600"}`}>{fmtEuro(data.total_planned - data.total_spent)}</span>
        </div>
      )}

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un mouvement (prestataire, montant, lieu…)" className="pl-9 bg-card" data-testid="tx-search-input" />
      </div>

      {loading && !data ? (
        <div className="h-24 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : visibleRows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{q ? "Aucun résultat pour cette recherche." : "Aucun budget ni mouvement. Définissez un « mois type » ou ajoutez une dépense."}</p>
      ) : (
        <>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Catégorie</TableHead>
              <TableHead className="w-44 text-right">Prévu</TableHead>
              <TableHead className="text-right">Dépensé / Reçu</TableHead>
              <TableHead className="text-right">Écart</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {monthlyVisible.map(renderExpenseRow)}
            {data && (
              <TableRow className="font-semibold bg-secondary/40 hover:bg-secondary/40">
                <TableCell>Total (dépenses budgétées)</TableCell>
                <TableCell className="text-right font-num">{fmtEuro(data.total_planned)}</TableCell>
                <TableCell className="text-right font-num">{fmtEuro(data.total_spent)}</TableCell>
                <TableCell className={`text-right font-num ${data.total_planned - data.total_spent < 0 ? "text-destructive" : "text-emerald-600"}`}>{fmtEuro(data.total_planned - data.total_spent)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {hasExceptional && (
          <div className="mt-6 rounded-xl border-2 border-amber-200 bg-amber-50/40 p-4" data-testid="exceptional-expenses-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><CalendarClock className="w-4 h-4" /></span>
              <div>
                <h3 className="font-display font-semibold text-base">Dépenses exceptionnelles</h3>
                <p className="text-xs text-muted-foreground">Provisions prévues dans « Vue Annuelle & Prévisions » et mouvements qui y sont rattachés.</p>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provision / Catégorie</TableHead>
                  <TableHead className="w-44 text-right">Prévu</TableHead>
                  <TableHead className="text-right">Dépensé</TableHead>
                  <TableHead className="text-right">Écart</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {frameExp.map((p) => {
                  const key = `exc-${p.id}`;
                  const allItems = txByPlanned[p.id] || [];
                  const items = allItems.filter(matchTx);
                  if (q && p.label.toLowerCase().indexOf(q) === -1 && items.length === 0) return null;
                  const depense = plannedSpentOf(p.id);
                  const ecart = round2(framePlanned(p) - depense);
                  const open = isOpen(key);
                  return (
                    <Fragment key={key}>
                      <TableRow data-testid={`exc-planned-row-${p.id}`} className="cursor-pointer hover:bg-amber-100/30">
                        <TableCell onClick={() => toggle(key)}>
                          <div className="flex items-center gap-1.5 font-medium">
                            {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                            {p.label}
                            {p.category_name && p.category_name !== "Non catégorisé" && <span className="text-xs text-muted-foreground font-normal">· {p.category_name}</span>}
                            {allItems.length > 0 ? <span className="text-xs text-muted-foreground font-normal">· {allItems.length} mvt</span> : <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">à lier</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-num">
                          {fmtEuro(framePlanned(p))}
                          {(p.months && p.months.filter((m) => m >= 1 && m <= 12).length > 1) && (
                            <div className="text-[10px] text-muted-foreground font-normal">{fmtEuro(p.amount)} / {p.months.filter((m) => m >= 1 && m <= 12).length} mois</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-num" onClick={() => toggle(key)}>{fmtEuro(depense)}</TableCell>
                        <TableCell className={`text-right font-num ${ecart < 0 ? "text-destructive" : "text-emerald-600"}`} onClick={() => toggle(key)}>{fmtEuro(ecart)}</TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="bg-amber-50/60 hover:bg-amber-50/60">
                          <TableCell colSpan={4} className="p-0">
                            <div className="px-4 py-1">
                              {items.length === 0 ? <p className="text-xs text-muted-foreground py-3">Aucune dépense liée. Modifiez un mouvement et choisissez cette provision pour la lier.</p> : items.map(renderTxLine)}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
                <TableRow className="font-semibold bg-amber-100/50 hover:bg-amber-100/50">
                  <TableCell>Total (exceptionnel)</TableCell>
                  <TableCell className="text-right font-num">{excPlannedTotal > 0 ? fmtEuro(excPlannedTotal) : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right font-num">{fmtEuro(excSpent)}</TableCell>
                  <TableCell className={`text-right font-num ${excPlannedTotal > 0 ? (excPlannedTotal - excSpent < 0 ? "text-destructive" : "text-emerald-600") : "text-muted-foreground"}`}>{excPlannedTotal > 0 ? fmtEuro(round2(excPlannedTotal - excSpent)) : "—"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        </>
      )}

      {incVisible.length > 0 && (
        <div className="mt-6 pt-5 border-t border-border" data-testid="monthly-income-section">
          <h3 className="font-display font-semibold text-base mb-3">Rentrées mensuelles</h3>
          {data && incVisible.length > 0 && (
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2 mb-3 text-sm" data-testid="monthly-remaining-income">
              <span className="font-medium">Reste à recevoir ce mois-ci (selon le prévu)</span>
              <span className={`font-num font-semibold ${data.total_income_planned - data.total_income_received > 0 ? "text-amber-600" : "text-emerald-600"}`}>{fmtEuro(Math.max(data.total_income_planned - data.total_income_received, 0))}</span>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Catégorie</TableHead>
                <TableHead className="w-44 text-right">Prévu</TableHead>
                <TableHead className="text-right">Reçu</TableHead>
                <TableHead className="text-right">Écart</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {incVisible.map((r) => {
                const open = isOpen(r.category_id);
                const items = incRowTxs(r.category_id);
                const hasPlan = r.planned !== null && r.planned !== undefined;
                const ecart = hasPlan ? r.received - r.planned : null;
                const key = r.category_id;
                return (
                  <Fragment key={key}>
                    <TableRow data-testid={`income-row-${key}`} className="cursor-pointer hover:bg-secondary/40">
                      <TableCell onClick={() => toggle(key)}>
                        <div className="flex items-center gap-1.5 font-medium">
                          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                          {r.category_name}
                          {r.is_override && <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 text-amber-600">modifié</Badge>}
                          {items.length > 0 && <span className="text-xs text-muted-foreground font-normal">· {items.length} mvt</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasPlan ? (
                          locked ? (
                            <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()} data-testid={`income-locked-${key}`}>
                              <Lock className="w-3 h-3 text-muted-foreground" /><span className="font-num">{fmtEuro(r.planned)}</span>
                            </div>
                          ) : renderBudgetEdit(r, key, "income")
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-num text-emerald-600" onClick={() => toggle(key)}>{r.received > 0 ? `+${fmtEuro(r.received)}` : fmtEuro(0)}</TableCell>
                      <TableCell className={`text-right font-num ${ecart !== null && ecart < 0 ? "text-destructive" : ecart !== null ? "text-emerald-600" : "text-muted-foreground"}`} onClick={() => toggle(key)}>{ecart !== null ? fmtEuro(ecart) : "—"}</TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className="bg-secondary/20 hover:bg-secondary/20">
                        <TableCell colSpan={4} className="p-0">
                          <div className="px-4 py-1">
                            {items.length === 0 ? (
                              <p className="text-xs text-muted-foreground py-3">Aucune rentrée dans cette catégorie ce mois-ci.</p>
                            ) : items.map((t) => (
                              <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0" data-testid={`transaction-row-${t.id}`}>
                                <div className="text-xs text-muted-foreground w-20 shrink-0">{fmtDate(t.date)}</div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm truncate flex items-center gap-2">{t.vendor || t.description || "—"} <MemberChips members={members} ids={t.member_ids} /></div>
                                  <div className="text-xs text-muted-foreground truncate">{[t.description && t.description !== t.vendor ? t.description : null, t.region].filter(Boolean).join(" · ")}</div>
                                </div>
                                <div className="font-num text-sm font-medium whitespace-nowrap text-emerald-600">+{fmtEuro(t.amount)}</div>
                                <div className="flex items-center gap-1">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit && onEdit(t)} data-testid={`edit-tx-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete && onDelete(t.id)} data-testid={`delete-tx-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              {data && (
                <TableRow className="font-semibold bg-secondary/40 hover:bg-secondary/40">
                  <TableCell>Total (rentrées)</TableCell>
                  <TableCell className="text-right font-num">{fmtEuro(data.total_income_planned)}</TableCell>
                  <TableCell className="text-right font-num text-emerald-600">{fmtEuro(data.total_income_received)}</TableCell>
                  <TableCell className={`text-right font-num ${data.total_income_received - data.total_income_planned < 0 ? "text-destructive" : "text-emerald-600"}`}>{fmtEuro(data.total_income_received - data.total_income_planned)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-display">Mois type — budget récurrent</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Ces montants sont proposés automatiquement chaque mois. Vous pourrez toujours les ajuster dans un mois précis.</p>
          <div className="space-y-2 py-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Dépenses</div>
            {expenseCats.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3">
                <Label className="text-sm font-normal flex-1">{c.name}<span className="text-muted-foreground text-xs"> · {c.group}</span></Label>
                <div className="relative w-28">
                  <Input type="text" inputMode="decimal" value={tpl[c.id] ?? ""} onChange={(e) => setTpl({ ...tpl, [c.id]: e.target.value })} placeholder="0" className="h-8 text-right pr-5 font-num" data-testid={`tpl-input-${c.id}`} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
                </div>
              </div>
            ))}
            {incomeCats.length > 0 && <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-3">Rentrées</div>}
            {incomeCats.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3">
                <Label className="text-sm font-normal flex-1">{c.name}<span className="text-muted-foreground text-xs"> · {c.group}</span></Label>
                <div className="relative w-28">
                  <Input type="text" inputMode="decimal" value={tpl[c.id] ?? ""} onChange={(e) => setTpl({ ...tpl, [c.id]: e.target.value })} placeholder="0" className="h-8 text-right pr-5 font-num" data-testid={`tpl-input-${c.id}`} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplOpen(false)}>Annuler</Button>
            <Button onClick={saveTpl} disabled={savingTpl} data-testid="save-template-button">{savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer le mois type"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
